import { useEffect, useState } from 'react';
import Modal from './Modals/Modal';
import { twMerge } from 'tailwind-merge';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import Text from './Text/Text';
import { Close } from '@drift-labs/icons';
import MarketIcon from './Utils/MarketIcon';
import { ENUM_UTILS } from '@drift/common';
import { PositionDirection } from '@drift-labs/sdk';
import useDriftActions from 'src/hooks/useDriftActions';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';

type SidePanelHeaderProps = {
	titleContent: React.ReactNode;
	infoContent: React.ReactNode;
	className?: string;
	showCloseBtn?: boolean;
	onClose?: () => void;
};

const SidePanelHeader = ({
	titleContent,
	infoContent,
	className,
	showCloseBtn,
	onClose,
}: SidePanelHeaderProps) => {
	return (
		<div
			className={twMerge(
				'flex flex-col p-4 gap-4 text-text-default relative bg-container-bg-hover',
				!infoContent && 'py-6',
				className
			)}
		>
			<Text.H3>{titleContent}</Text.H3>
			{infoContent}
			{showCloseBtn && onClose && (
				<div
					onClick={onClose}
					className="absolute cursor-pointer top-5 right-6"
				>
					<Close />
				</div>
			)}
		</div>
	);
};

const MarketPosition = ({
	marketSymbol,
	direction,
}: {
	marketSymbol: string;
	direction: 'long' | 'short' | 'buy' | 'sell';
}) => {
	return (
		<div className="flex items-center gap-1">
			<div>
				<MarketIcon marketSymbol={marketSymbol} sizeClass="w-6 h-6" />
			</div>
			<div className="flex flex-col">
				<div className="flex items-center gap-2">
					<Text.BODY2>{marketSymbol}</Text.BODY2>
				</div>
				<Text.MICRO1
					className={twMerge(
						'uppercase',
						direction === ENUM_UTILS.toStr(PositionDirection.LONG)
							? 'text-positive-green'
							: 'text-negative-red'
					)}
				>
					{direction}
				</Text.MICRO1>
			</div>
		</div>
	);
};

const DetailBox = ({
	label,
	value,
	className,
}: {
	label: string;
	value: React.ReactNode;
	className?: string;
}) => {
	return (
		<div className={twMerge('flex flex-col gap-1', className)}>
			<Text.BODY3 className="text-text-label">{label}</Text.BODY3>
			<Text.BODY3>{value}</Text.BODY3>
		</div>
	);
};

const SidePanel = ({
	children,
	className,
	side = 'left',
}: {
	children: React.ReactNode;
	onClose: () => void;
	className?: string;
	side?: 'left' | 'right';
}) => {
	const isClosing = useDriftStore((s) => s.sidebarModalIsClosing);
	const actions = useDriftActions();
	const isMobile = useIsMobileScreenSize();
	const [isMounted, setIsMounted] = useState(false); // used for transitions

	useEffect(() => {
		if (!isClosing) {
			setTimeout(() => {
				setIsMounted(true);
			}, 50);
		} else {
			setIsMounted(false);
		}

		return () => {
			setIsMounted(false);
		};
	}, [isClosing]);

	const onTransitionClose = () => {
		actions.hideSidebarModalWithTimeout();
	};

	const getTopBarHeight = () => {
		const topBar = document.getElementById('topbar');
		if (topBar) {
			const { bottom } = topBar.getBoundingClientRect();
			return bottom;
		}
		return 0;
	};

	return (
		<Modal
			onClose={onTransitionClose}
			grid={false}
			className={twMerge(
				'absolute right-0 top-0 bottom-0 w-full sm:w-auto h-auto rounded-none'
			)}
			noBackground
		>
			<Modal.Body
				className={twMerge(
					'w-full transform transition-transform duration-300 absolute rounded-none md:rounded-none flex flex-col bottom-0 bg-main-bg pb-0 border-t border-b-0 border-container-border',
					!isMounted && 'translate-x-full',
					side === 'left' && 'right-0 border-l',
					side == 'right' && 'left-0 border-r',
					className
				)}
				style={{
					top: isMobile ? 0 : getTopBarHeight(),
				}}
			>
				{children}
			</Modal.Body>
		</Modal>
	);
};

SidePanel.Header = SidePanelHeader;
SidePanel.MarketPosition = MarketPosition;
SidePanel.DetailBox = DetailBox;

export default SidePanel;
